import { UseFormRegister, UseFormReturn } from "react-hook-form";

import { FancyLabelAndInput, FancyLabelAndTextArea } from "../../components/reuseables/form.comp";
import { useTranslation } from "../../core/translation.core";
import { useSpecialists } from "../../hooks/services/useSpecialist";
import { FancyCheckbox } from "../reuseables/checkbox.comp";
import { selectCss } from "../setting/posShopSelector";
import { PosUserEyeInfoFormData } from "../../types/user";

interface EyesInfoEditorProps {
  methods: UseFormReturn<PosUserEyeInfoFormData>;
  handleCancel?: VoidFunction;
  handleSubmit: (data: PosUserEyeInfoFormData & { isSave?: boolean }) => Promise<void>;
  placement?: "default" | "order";
}

export default function EyesInfoEditor({ methods, handleCancel, handleSubmit, placement = "default" }: EyesInfoEditorProps) {
  const { t, msg } = useTranslation();

  const sections = [
    {
      fields: [
        { label: "Sphere", nameLeft: "habRxSphLeft", nameRight: "habRxSphRight" },
        { label: "Cylinder", nameLeft: "habRxCylLeft", nameRight: "habRxCylRight" },
        { label: "Axis", nameLeft: "habRxAxisLeft", nameRight: "habRxAxisRight" },
        { label: "ADD", nameLeft: "habRxAddLeft", nameRight: "habRxAddRight" },
        { label: "V.A.", nameLeft: "habRxVaLeft", nameRight: "habRxVaRight" },
        { label: "Prism", nameLeft: "habRxPrismLeft", nameRight: "habRxPrismRight" },
        { label: "PD", nameLeft: "habRxPDLeft", nameRight: "habRxPDRight" },
        { label: "Height", nameLeft: "habRxHeightLeft", nameRight: "habRxHeightRight" },
      ],
      title: "HAB. RX",
    },
    {
      fields: [
        { label: "Sphere", nameLeft: "fullRxSphLeft", nameRight: "fullRxSphRight" },
        { label: "Cylinder", nameLeft: "fullRxCylLeft", nameRight: "fullRxCylRight" },
        { label: "Axis", nameLeft: "fullRxAxisLeft", nameRight: "fullRxAxisRight" },
        { label: "ADD", nameLeft: "fullRxAddLeft", nameRight: "fullRxAddRight" },
        { label: "V.A.", nameLeft: "fullRxVaLeft", nameRight: "fullRxVaRight" },
        { label: "Prism", nameLeft: "fullRxPrismLeft", nameRight: "fullRxPrismRight" },
        { label: "PD", nameLeft: "fullRxPDLeft", nameRight: "fullRxPDRight" },
        { label: "Height", nameLeft: "fullRxHeightLeft", nameRight: "fullRxHeightRight" },
      ],
      title: "FULL. RX",
    },
    {
      fields: [
        { label: "Sphere", nameLeft: "adjustedRxSphLeft", nameRight: "adjustedRxSphRight" },
        { label: "Cylinder", nameLeft: "adjustedRxCylLeft", nameRight: "adjustedRxCylRight" },
        { label: "Axis", nameLeft: "adjustedRxAxisLeft", nameRight: "adjustedRxAxisRight" },
        { label: "ADD", nameLeft: "adjustedRxAddLeft", nameRight: "adjustedRxAddRight" },
        { label: "V.A.", nameLeft: "adjustedRxVaLeft", nameRight: "adjustedRxVaRight" },
        { label: "Prism", nameLeft: "adjustedRxPrismLeft", nameRight: "adjustedRxPrismRight" },
        { label: "PD", nameLeft: "adjustedRxPDLeft", nameRight: "adjustedRxPDRight" },
        { label: "Height", nameLeft: "adjustedRxHeightLeft", nameRight: "adjustedRxHeightRight" },
      ],
      title: "ADJUSTED RX",
    },
  ];

  return (
    <form className="space-y-6">
      {sections.map(({ title, fields }, idx) => (
        <EyeInfoSection key={idx} title={title} fields={fields} methods={methods} />
      ))}

      <OptometristSelector register={methods.register} />

      <FancyLabelAndTextArea rows={3} name="remarks" methods={methods} label={t(msg.customer.remarks)} />

      {placement === "order" && (
        <FancyCheckbox value={true} register={methods.register("isSave")} labelId="is-save">
          {t(msg.checkout.saveEyeInfoToDatabase)}
        </FancyCheckbox>
      )}

      <div className="flex justify-between gap-4">
        {handleCancel && (
          <button onClick={handleCancel} className="flex-center gap-3 bg-gray-300 text-white py-3 px-5 w-full rounded-md">
            <span>{t(msg.form.discard)}</span>
          </button>
        )}

        <button
          type="button"
          id="eyeinfo-submit-button"
          onClick={methods.handleSubmit(handleSubmit)}
          className="flex-center gap-3 bg-her-blue text-white py-3 px-5 w-full rounded-md"
        >
          <span>{t(msg.form.saveInformation)}</span>
        </button>
      </div>
    </form>
  );
}

interface EyeInfoSectionProps {
  title: string;
  fields: { nameLeft: string; nameRight: string; label: string }[];
  methods: any;
}

function EyeInfoSection({ title, fields, methods }: EyeInfoSectionProps) {
  return (
    <div className="space-y-6">
      <h1 className="font-medium bg-slate-200 dark:bg-gray-500 p-3">{title}</h1>

      <div className="overflow-x-auto space-y-6 pb-4">
        <table className="min-w-full table-auto border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 px-4 py-2">Eye</th>
              {fields.map((field, idx) => (
                <th key={idx} className="border border-gray-300 px-4 py-2 min-w-[100px]">
                  {field.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            <tr>
              <td className="border border-gray-300 px-4 py-2">Right</td>
              {fields.map((field, idx) => (
                <td key={idx} className="border border-gray-300 px-4 py-2">
                  <FancyLabelAndInput type="text" methods={methods} name={field.nameRight} />
                </td>
              ))}
            </tr>

            <tr>
              <td className="border border-gray-300 px-4 py-2">Left</td>
              {fields.map((field, idx) => (
                <td key={idx} className="border border-gray-300 px-4 py-2">
                  <FancyLabelAndInput type="text" methods={methods} name={field.nameLeft} />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

const OptometristSelector = ({ register }: { register: UseFormRegister<PosUserEyeInfoFormData> }) => {
  const { t, msg } = useTranslation();

  const { data: optometrists } = useSpecialists("optometrist");

  if (!optometrists) return null;
  return (
    <div className="mt-4">
      <label className="block">{t(msg.customer.optometrist)}</label>
      <select className={selectCss} {...register("optometristId")}>
        <option key="none" value=""></option>
        {optometrists.map((d) => (
          <option key={d.id} value={d.id}>{`${d.firstName} ${d.lastName}`}</option>
        ))}
      </select>
    </div>
  );
};
